import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap"
import { setCompanyScope } from "../store/company/actions"

// The whole Operations + Developer area is scope-less (manages companies/groups,
// users, roles, app-wide settings — not data inside one company), so hide the switcher.
// Camps are global too (not company-scoped), so the switcher is hidden there as well.
const SCOPELESS_ROUTES = [
  "/admin",
  "/developer",
  "/user-management",
  "/company-management",
  "/settings-roles",
  "/camp",
  "/fleet",
]

const Row = ({ label, count, indent, active, isGroup, onClick }) => {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        width: "100%", padding: indent ? "7px 14px 7px 30px" : "8px 14px",
        textAlign: "left", fontSize: 13, lineHeight: "18px", border: "none", cursor: "pointer",
        color: active ? "var(--accent-clr, #4338ca)" : "#374151",
        fontWeight: active ? 700 : isGroup ? 600 : 400,
        background: active ? "var(--menu-avtive, #eef2ff)" : hover ? "#f3f4f6" : "transparent",
        transition: "background-color 120ms ease",
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {isGroup ? "▣ " : ""}{label}
      </span>
      {isGroup && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", whiteSpace: "nowrap" }}>
          {count} co.
        </span>
      )}
    </button>
  )
}

/**
 * Multi-company tenancy: header "VIEWING" switcher (StaffFlow parity). Pick a single
 * company (scopes the UI to that entity) or a Group (consolidated across its companies).
 * Persists the scope (read by api_helper as X-Scope headers) and reloads.
 */
const CompanySelector = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const scope = useSelector(state => state.Company)

  const ctx = useSelector(state => state.Login?.loggedUser?.company_context) ||
    (() => {
      try { return JSON.parse(localStorage.getItem("authUser") || "{}")?.company_context } catch (e) { return null }
    })()

  const companies = ctx?.companies || []
  const groups = ctx?.groups || []

  if (!companies.length && !groups.length) return null
  if (SCOPELESS_ROUTES.some(p => pathname.startsWith(p))) return null

  const inGroup = gid => companies.filter(c => c.group_id === gid)
  const standalone = companies.filter(c => !c.group_id)

  const choose = (type, id) => {
    setOpen(false)
    if (scope?.scopeType === type && scope?.scopeId === id) return
    dispatch(setCompanyScope(type, id))
    window.location.reload() // refetch everything under the new scope
  }
  const isActive = (type, id) => scope?.scopeType === type && scope?.scopeId === id

  // Trigger label: "<group code> · all" when a group is selected, else "<company code> only".
  const isGroupScope = scope?.scopeType === "group"
  const current = isGroupScope
    ? groups.find(g => g.id === scope?.scopeId)
    : companies.find(c => c.id === scope?.scopeId)
  const triggerText = current
    ? isGroupScope ? `${current.code} · all` : `${current.code} only`
    : "Select"

  return (
    <Dropdown isOpen={open} toggle={() => setOpen(!open)} className="d-inline-block ms-2 me-2">
      <DropdownToggle
        tag="button"
        title="Changes what you see on dashboards & reports. It does not change any settings."
        style={{
          display: "flex", alignItems: "center", gap: 8, maxWidth: 240,
          border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, padding: "5px 12px",
          color: isGroupScope ? "var(--accent-clr, #4338ca)" : "#111827", fontWeight: 600, fontSize: 12.5,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#9ca3af" }}>Viewing</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{triggerText}</span>
        <i className="uil-angle-down" style={{ color: "#9ca3af", fontSize: 16 }} />
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-end p-0" style={{ minWidth: 270, maxHeight: 420, overflowY: "auto" }}>
        <div style={{ padding: "8px 14px", fontSize: 11, lineHeight: "15px", color: "#9ca3af", borderBottom: "1px solid #f3f4f6" }}>
          Sets what you see on dashboards &amp; reports. Doesn&apos;t change settings.
        </div>
        {groups.map((g, i) => (
          <div key={`grp-${g.id}`}>
            {i > 0 && <div style={{ borderTop: "1px solid #f3f4f6" }} />}
            <Row label={g.name} count={inGroup(g.id).length} isGroup active={isActive("group", g.id)} onClick={() => choose("group", g.id)} />
            {inGroup(g.id).map(c => (
              <Row key={`c-${c.id}`} label={`${c.code} · ${c.company_name}`} indent active={isActive("company", c.id)} onClick={() => choose("company", c.id)} />
            ))}
          </div>
        ))}
        {standalone.length > 0 && (
          <>
            {groups.length > 0 && <div style={{ borderTop: "1px solid #f3f4f6" }} />}
            <div style={{ padding: "6px 14px", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "#9ca3af" }}>Standalone</div>
            {standalone.map(c => (
              <Row key={`s-${c.id}`} label={`${c.code} · ${c.company_name}`} active={isActive("company", c.id)} onClick={() => choose("company", c.id)} />
            ))}
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  )
}

export default CompanySelector
